<template>
  <v-container>
    <div
      style="
        display: grid;
        grid-template-columns: auto auto;
        justify-content: space-between;
      ">
      <div>
        <span v-if="childTypes && !spaceStore.readOnly">
          <v-btn
            v-for="childType in childTypes"
            :key="childType"
            class="mr-2 text-capitalize"
            color="primary"
            flat
            prepend-icon="mdi-plus"
            rounded="xl"
            variant="outlined"
            @click="createAction(childType)"
            :data-cy="`create-${childType}-button`">
            {{ $t(`entities.${childType}.name`) }}
          </v-btn>
        </span>

        <v-btn
          v-if="authStore.isSuperUser && props.nodeId.endsWith('folder')"
          class="mr-2 text-capitalize"
          color="primary"
          flat
          prepend-icon="mdi-download"
          rounded="xl"
          variant="outlined"
          @click="downloadAction"
          data-cy="download-folder-button">
          Download
        </v-btn>
        <v-btn
          v-if="childTypes && childTypes.includes('machine')"
          class="mr-2 text-capitalize"
          color="primary"
          flat
          prepend-icon="mdi-content-copy"
          rounded="xl"
          variant="outlined"
          @click="dialog = true"
          data-cy="download-manufacturing-step-button">
              {{ $t(`entities.machine.name`) }}
        </v-btn>
      </div>
      <div>
        <v-btn
          class="mr-2 text-capitalize"
          color="primary"
          flat
          prepend-icon="mdi-content-copy"
          rounded="xl"
          variant="outlined"
          @click="copyAction"
          data-cy="copy-button">
          {{ $t("actions.copy") }}
        </v-btn>

        <template v-if="!spaceStore.readOnly">
          <v-btn
            v-if="copyType && childTypes && childTypes.includes(copyType)"
            class="mr-2 text-capitalize"
            color="primary"
            flat
            prepend-icon="mdi-content-paste"
            rounded="xl"
            variant="outlined"
            @click="pasteAction"
            data-cy="paste-button">
            {{
              $t("actions.paste", {
                element: mainStore.copyData.name
              })
            }}
          </v-btn>
          <v-btn
            v-if="copyType === 'part' && route.params.nodeId.endsWith('part')"
            class="mr-2 text-capitalize"
            color="primary"
            flat
            prepend-icon="mdi-content-paste"
            rounded="xl"
            variant="outlined"
            @click="pasteVariantAction"
            data-cy="paste-button">
            {{
              $t("actions.paste", {
                element: mainStore.copyData.variantName
              })
            }}
          </v-btn>

          <DeleteWithConfirmationButton
            v-if="!hideDelete"
            :delete-action="deleteAction"
            :disabled-message="disabledDeleteMessage"
            :disabled="disabledDelete" />
        </template>
      </div>
    </div>
<v-dialog v-model="dialog">
    <MachineSearch @select="machineCopyAction" @cancel="dialog = false"></MachineSearch>
</v-dialog>

  </v-container>
</template>

<script setup>
import { useTranslation } from "i18next-vue";
import { ref, toRaw } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useConfirm, useSnackbar } from "vuetify-use-dialog";

import DeleteWithConfirmationButton from "@/components/tools/DeleteWithConfirmationButton.vue";
import { useAuthStore } from "@/stores/authStore";
import { useSpaceStore } from "@/stores/spaceStore";
import { useMainStore } from "@/stores/mainStore";
import { computed } from "vue";
import { useTreeStore } from "@/stores/treeStore";
import fieldByPart from "@/data/generated/fieldByPart.json";
import MachineSearch from "../dialogs/MachineSearch.vue";

const spaceStore = useSpaceStore();
const authStore = useAuthStore();
const treeStore = useTreeStore();
const mainStore = useMainStore();
const router = useRouter();
const route = useRoute();
const { t } = useTranslation();

const props = defineProps({
  valid: {
    type: Boolean,
    default: false
  },
  nodeId: {
    type: String,
    required: true
  },
  childTypes: {
    type: Array,
    required: false,
    default: null
  },
  reset: {
    type: Function,
    required: false,
    default: null
  },
  disabledDelete: {
    type: Boolean,
    required: false,
    default: false
  },
  disabledDeleteMessage: {
    type: String,
    default: "deleteDisabled"
  },
  hideDelete: {
    type: Boolean,
    required: false,
    default: false
  },
  hideSubmit: {
    type: Boolean,
    required: false,
    default: false
  },
  customDeleteAction: {
    type: Function,
    required: false,
    default: null
  }
});

const node = ref(spaceStore.getElement(props.nodeId));

const deleteAction = () => {
  if (props.customDeleteAction) {
    props.customDeleteAction();
  } else {
    spaceStore.removeElement(props.nodeId);
  }
};

const createAction = (childType) => {
  router.push({
    name: "calculations-" + childType,
    params: {
      nodeId: spaceStore.createElement(childType, props.nodeId)
    }
  });
};

const downloadAction = () => {
  // fetch all elements that are connected to this
  const ids = [];
  const result = {};
  const rustResult = {};
  const inputResult = [];

  const recursiveSearch = (id) => {
    const element = structuredClone(toRaw(spaceStore.getElement(id)));
    if (ids.includes(id) || !element) {
      return;
    }
    ids.push(id);
    delete element.user_id;

    result[id] = toRaw(element);

    let t = id.split("_")[1];
    rustResult[id] = {
      children: (element.children || []).filter((c) => c),
      parent: element.parent || "",
      location: element.location || "",
      id: id,
      data: Object.fromEntries(
        Object.entries(structuredClone(element)).filter(
          ([key, value]) => key.endsWith(t) && value !== null
        )
      ),
      extra_data: {
        level: (element.level || 0).toString(),
        shift_model_part: element.shift_model_part,
        activity_labour: element.activity_labour
      }
    };

    for (const key of Object.keys(rustResult[id].data)) {
      if (fieldByPart[t].inputs.includes(key)) {
        inputResult.push(id + "|" + key + "|" + rustResult[id].data[key]);
      }
    }

    delete rustResult[id].data.shift_model_part;
    delete rustResult[id].data.activity_labour;

    recursiveSearch(element.location);
    for (const i of element.children) {
      recursiveSearch(i);
    }
  };

  recursiveSearch(props.nodeId);

  console.log("Hier", result);
  console.log("Rust", rustResult);
  console.log("Input", inputResult);
  navigator.clipboard.writeText(JSON.stringify(result));
};

const copyType = computed(() => {
  return mainStore.copyData.id ? mainStore.copyData.id.split("_")[1] : null;
});

function copyAction() {
  const id = props.nodeId
    .replace("variant", "part")
    .replace("manufacturingStep", "part");

  spaceStore.prepareClone(id);
}

const createConfirm = useConfirm();
const createSnackbar = useSnackbar();

function pasteAction() {
  if (
    mainStore.copyData.spaceId !== mainStore.activeSpaceId &&
    (mainStore.copyData.id.endsWith("_part") ||
      mainStore.copyData.id.endsWith("_folder")) &&
    mainStore.copyData.locations.length
  ) {
    createConfirm({
      dialogProps: { width: 400 },
      cardProps: { color: "green" },
      title: t(`actions.confirmation.copy-location.title`),
      content: t(`actions.confirmation.copy-location.content`),
      confirmationText: t(`actions.confirmation.copy-location.confirm`),
      cancellationText: t(`actions.confirmation.copy-location.cancel`),
      confirmationButtonProps: {}
    }).then((confirm) => {
      spaceStore.cloneOnServer(props.nodeId, !!confirm);
    });
  } else {
    spaceStore.cloneOnServer(props.nodeId, false);
  }
}

function pasteVariantAction() {
  if (
    mainStore.copyData.spaceId !== mainStore.activeSpaceId &&
    (mainStore.copyData.id.endsWith("_part") ||
      (mainStore.copyData.id.endsWith("_folder") &&
        mainStore.copyData.locations.length))
  ) {
    createConfirm({
      dialogProps: { width: 400 },
      cardProps: { color: "green" },
      title: t(`actions.confirmation.copy-location.title`),
      content: t(`actions.confirmation.copy-location.content`),
      confirmationText: t(`actions.confirmation.copy-location.confirm`),
      cancellationText: t(`actions.confirmation.copy-location.cancel`),
      confirmationButtonProps: {}
    }).then((confirm) => {
      spaceStore.cloneOnServer(props.nodeId, !!confirm, true);
    });
  } else {
    spaceStore.cloneOnServer(props.nodeId, false, true);
  }
}

const dialog = ref(false)

function machineCopyAction(id) {
  dialog.value = false;
  router.push({
    name: "calculations-machine",
    params: {
      nodeId: spaceStore.createElement("machine", props.nodeId, id)
    }
  });


}
</script>
